import { useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { useLoan } from '@/features/loan/useLoan';
import { OnboardingPage } from '@/features/loan/OnboardingPage';
import { HomePage } from '@/features/loan/HomePage';
import { SettingsPage } from '@/features/loan/SettingsPage';
import { TimelinePage } from '@/features/timeline/TimelinePage';
import { SchedulePage } from '@/features/schedule/SchedulePage';
import { RecordsPage } from '@/features/loan/RecordsPage';
import { BottomNav } from './BottomNav';
import { AppHeader } from './AppHeader';
import { runAutoBackupIfDue } from '@/lib/driveAutoBackup';
import { buildCurrentBackup } from '@/lib/currentBackup';

/**
 * App root. Gates all real navigation behind having a Loan record —
 * per spec, the app starts with one editable Draft/Expected loan record,
 * so on very first launch (no loan yet) we show onboarding instead of
 * the normal tab shell.
 */
export default function App() {
  const { loan, isLoading, refresh } = useLoan();

  // Automatic Google Drive backup check on app open. Fire-and-forget: it
  // never throws, only reads local data, and skips immediately (before
  // touching the database or the network) unless Drive is connected in
  // this session — the access token lives in memory only and is never
  // refreshed silently, so on a cold open this is normally a no-op; the
  // backup itself happens when Drive is connected (see RecordsPage's
  // Backup & Restore section). The guard inside runAutoBackupIfDue
  // makes React StrictMode's double effect invocation harmless.
  useEffect(() => {
    void runAutoBackupIfDue({ buildBackup: buildCurrentBackup });
  }, []);

  if (isLoading) {
    return (
      <div className="app-shell">
        <AppHeader />
        <main className="app-main">
          <p className="text-muted">Loading…</p>
        </main>
      </div>
    );
  }

  if (!loan) {
    return (
      <div className="app-shell">
        <AppHeader />
        <main className="app-main">
          <OnboardingPage onComplete={refresh} />
        </main>
      </div>
    );
  }

  return (
    <div className="app-shell">
      <AppHeader />
      <main className="app-main">
        <Routes>
          <Route path="/" element={<HomePage loan={loan} />} />
          <Route path="/timeline" element={<TimelinePage loan={loan} />} />
          <Route path="/schedule" element={<SchedulePage loan={loan} />} />
          <Route path="/records" element={<RecordsPage loan={loan} onRestored={refresh} />} />
          <Route path="/settings" element={<SettingsPage loan={loan} onLoanUpdated={refresh} />} />
        </Routes>
      </main>
      <BottomNav />
    </div>
  );
}
